import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, setDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Lightweight data context for Insurance Estimator only.
 * Only loads insurance_estimates collection, not all 14 collections.
 */

const InsuranceDataContext = createContext(null);

export const useInsuranceData = () => {
  const context = useContext(InsuranceDataContext);
  if (!context) {
    throw new Error('useInsuranceData must be used within an InsuranceDataProvider');
  }
  return context;
};

export const InsuranceDataProvider = ({ children }) => {
  const [insuranceEstimates, setInsuranceEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load only insurance estimates
  const loadEstimates = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'insurance_estimates'));
      const estimates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      estimates.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
      setInsuranceEstimates(estimates);
    } catch (err) {
      console.error('Error loading insurance estimates:', err);
      setError(err.message);
      setInsuranceEstimates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEstimates();
  }, [loadEstimates]);

  // Add insurance estimate
  const addInsuranceEstimate = useCallback(async (estimateData) => {
    try {
      const docRef = await addDoc(collection(db, 'insurance_estimates'), {
        ...estimateData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      const newEstimate = { id: docRef.id, ...estimateData };
      setInsuranceEstimates(prev => [newEstimate, ...prev]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding insurance estimate:', err);
      throw err;
    }
  }, []);

  // Update insurance estimate
  const updateInsuranceEstimate = useCallback(async (estimateId, updates) => {
    try {
      await setDoc(doc(db, 'insurance_estimates', estimateId), {
        ...updates,
        updatedAt: Timestamp.now(),
      }, { merge: true });
      setInsuranceEstimates(prev => prev.map(e =>
        e.id === estimateId ? { ...e, ...updates } : e
      ));
    } catch (err) {
      console.error('Error updating insurance estimate:', err);
      throw err;
    }
  }, []);

  // Get single insurance estimate
  const getInsuranceEstimate = useCallback((estimateId) => {
    return insuranceEstimates.find(e => e.id === estimateId);
  }, [insuranceEstimates]);

  const value = {
    insuranceEstimates,
    loading,
    error,
    addInsuranceEstimate,
    updateInsuranceEstimate,
    getInsuranceEstimate,
    refreshEstimates: loadEstimates,
  };

  return (
    <InsuranceDataContext.Provider value={value}>
      {children}
    </InsuranceDataContext.Provider>
  );
};

export default InsuranceDataContext;
